import { useEffect, useState, type ReactNode } from 'react';
import { IconAlert, IconMoon, IconSun } from './icons';

/** Marca em mono, como no site: "licita" + ".dev" na cor de acento. */
export function Wordmark({ size = 14 }: { size?: number }) {
  return (
    <span className="inline-flex items-center gap-2 font-mono font-semibold tracking-tight" style={{ fontSize: size }}>
      <svg width={size + 6} height={size + 6} viewBox="0 0 64 64" aria-hidden="true">
        <rect width="64" height="64" rx="14" fill="#0A0B10" />
        <path d="M20 14v30h20" fill="none" stroke="#ECEDF2" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M30 38l6 6 12-14" fill="none" stroke="#7B86FF" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <span>
        licita<span className="text-accent">.dev</span>
      </span>
    </span>
  );
}

type Theme = 'light' | 'dark';
const currentTheme = (): Theme => (document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light');

/** Mesmo mecanismo do site: data-theme no <html> + localStorage('theme') (lido por /theme.js). */
export function ThemeToggle() {
  const [theme, setTheme] = useState<Theme>('light');
  useEffect(() => {
    setTheme(currentTheme());
    const mo = new MutationObserver(() => setTheme(currentTheme()));
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => mo.disconnect();
  }, []);
  const toggle = () => {
    const t: Theme = theme === 'dark' ? 'light' : 'dark';
    document.documentElement.dataset.theme = t;
    try {
      localStorage.setItem('theme', t);
    } catch {}
  };
  return (
    <button
      type="button"
      onClick={toggle}
      aria-label="Alternar tema claro/escuro"
      aria-pressed={theme === 'dark'}
      title="Alternar tema"
      className="app-btn app-btn-ghost size-8 shrink-0 px-0 text-fg-2"
    >
      {theme === 'dark' ? <IconSun /> : <IconMoon />}
    </button>
  );
}

export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: ReactNode; actions?: ReactNode }) {
  return (
    <header className="mb-5 flex flex-wrap items-end justify-between gap-3 border-b border-line pb-4">
      <div>
        <h1 className="text-[18px] font-semibold tracking-[-0.2px]">{title}</h1>
        {subtitle && <p className="mt-0.5 text-fg-2">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </header>
  );
}

export function ErrorBox({ children }: { children: ReactNode }) {
  return (
    <p role="alert" className="flex items-start gap-2 rounded-lg px-3 py-2 text-[13px] text-bad" style={{ background: 'var(--bad-soft)' }}>
      <span className="mt-px shrink-0">
        <IconAlert size={15} />
      </span>
      <span>{children}</span>
    </p>
  );
}

export function Notice({ children }: { children: ReactNode }) {
  return (
    <p role="status" className="rounded-lg px-3 py-2 text-[13px] text-accent" style={{ background: 'var(--accent-soft)' }}>
      {children}
    </p>
  );
}

const TONES = { ok: 'app-chip-ok', warn: 'app-chip-warn', bad: 'app-chip-bad', info: 'app-chip-accent', muted: '' } as const;
export function Chip({ tone = 'muted', children }: { tone?: keyof typeof TONES; children: ReactNode }) {
  return <span className={`app-chip ${TONES[tone]}`}>{children}</span>;
}

export function Skeleton({ h = 120 }: { h?: number }) {
  return <div className="animate-pulse rounded-lg bg-bg3 motion-reduce:animate-none" style={{ height: h }} aria-busy="true" />;
}

/** Placeholder das páginas que ainda não existem (Editais, Sincronização). */
export function EmptyState({ children }: { children: ReactNode }) {
  return <div className="app-card border-dashed text-center text-fg-2">{children}</div>;
}

export const th = 'px-2.5 py-1.5 text-left text-[11px] font-medium uppercase tracking-[0.5px] text-mute';
export const td = 'px-2.5 py-1.5 align-middle';
